/**
 * Cliente de telemetría — engancha window.onerror / unhandledrejection /
 * performance observers y empuja eventos al backend vía /stability/collect/**.
 *
 * Falla silente: si no hay sesión activa o el backend responde error, simplemente
 * no envía. La promesa de "telemetría jamás rompe el flujo principal" del estándar
 * §7 se cumple aquí.
 */
import { stabilityApi } from "@/services/backend";
import { isAuthenticated } from "@/services/auth";

let sessionId: string | null = null;
let telemetryDisabled = false;

export function ensureSessionId(): string {
  if (sessionId) return sessionId;
  let stored = sessionStorage.getItem("aequia.sid");
  if (!stored) {
    stored = crypto.randomUUID();
    sessionStorage.setItem("aequia.sid", stored);
  }
  sessionId = stored;
  return stored;
}

function safeReportCrash(
  payload: Parameters<typeof stabilityApi.reportCrash>[0],
) {
  if (telemetryDisabled || !isAuthenticated()) return;
  void stabilityApi.reportCrash(payload).catch(() => {
    telemetryDisabled = true;
  });
}

function safeReportLatency(
  payload: Parameters<typeof stabilityApi.reportLatency>[0],
) {
  if (telemetryDisabled || !isAuthenticated()) return;
  void stabilityApi.reportLatency(payload).catch(() => {
    telemetryDisabled = true;
  });
}

function safeReportUsage(
  payload: Parameters<typeof stabilityApi.reportUsage>[0],
) {
  if (telemetryDisabled || !isAuthenticated()) return;
  void stabilityApi.reportUsage(payload).catch(() => {
    telemetryDisabled = true;
  });
}

function fingerprintOf(message: string): string {
  let hash = 0;
  for (let i = 0; i < message.length; i++)
    hash = ((hash << 5) - hash + message.charCodeAt(i)) | 0;
  return `js-${Math.abs(hash).toString(16)}`;
}

export function deviceMeta() {
  const ua = navigator.userAgent;
  return {
    deviceType: /Mobi|Android|iPhone|iPad/.test(ua) ? "mobile" : "desktop",
    browserName: /Edg\//.test(ua)
      ? "Edge"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Firefox\//.test(ua)
          ? "Firefox"
          : /Safari\//.test(ua)
            ? "Safari"
            : "Unknown",
    osName: /Mac OS X/.test(ua)
      ? "macOS"
      : /Windows/.test(ua)
        ? "Windows"
        : /Linux/.test(ua)
          ? "Linux"
          : /Android/.test(ua)
            ? "Android"
            : /iPhone|iPad/.test(ua)
              ? "iOS"
              : "Unknown",
  };
}

let installed = false;

export function installTelemetry() {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    const msg = event.message || (event.error?.message ?? "unknown");
    safeReportCrash({
      fingerprint: fingerprintOf(msg),
      severity: "ERROR",
      message: msg.slice(0, 1000),
      stackTrace:
        event.error?.stack ??
        `${event.filename}:${event.lineno}:${event.colno}`,
      metadata: {
        source: "window.onerror",
        url: window.location.pathname + window.location.search,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg =
      typeof reason === "string"
        ? reason
        : (reason?.message ?? "unhandled promise rejection");
    safeReportCrash({
      fingerprint: fingerprintOf(msg),
      severity: "ERROR",
      message: msg.slice(0, 1000),
      stackTrace: reason?.stack ?? null,
      metadata: {
        source: "unhandledrejection",
        url: window.location.pathname + window.location.search,
      },
    });
  });

  if ("PerformanceObserver" in window) {
    try {
      const obs = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (
            entry.entryType === "navigation" &&
            entry instanceof PerformanceNavigationTiming
          ) {
            safeReportLatency({
              category: "navigation",
              operation: "page-load",
              path: window.location.pathname + window.location.search,
              durationMs: Math.round(entry.loadEventEnd - entry.startTime),
              success: true,
              metadata: {
                domContentLoaded: Math.round(
                  entry.domContentLoadedEventEnd - entry.startTime,
                ),
              },
            });
          }
        });
      });
      obs.observe({ entryTypes: ["navigation"] });
    } catch {
      /* unsupported */
    }
  }

  // primer page view (subsequent navigations are tracked by
  // <TelemetryRouteTracker /> mounted dentro del RouterProvider).
  safeReportUsage({
    sessionId: ensureSessionId(),
    ...deviceMeta(),
    path: window.location.pathname + window.location.search,
  });
}

export function reportCrash(
  error: unknown,
  metadata: Record<string, unknown> = {},
): void {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "unknown error";
  safeReportCrash({
    fingerprint: fingerprintOf(message),
    severity: "ERROR",
    message: message.slice(0, 1000),
    stackTrace: error instanceof Error ? error.stack : undefined,
    metadata: {
      source: "react-error-boundary",
      url: window.location.pathname + window.location.search,
      ...metadata,
    },
  });
}

/** Reporta una vista hacia el backend de estabilidad. Exportado para
 * el componente <TelemetryRouteTracker /> que escucha los cambios de
 * pathname expuestos por React Router. */
export function reportRouteChange(path: string): void {
  safeReportUsage({
    sessionId: ensureSessionId(),
    ...deviceMeta(),
    path,
  });
}
